import { normalizeDirectoryText } from "./directory-categories";

/**
 * Palabras clave de busqueda de un negocio. Reglas del producto:
 *   * Opcionales, maximo 3 por negocio.
 *   * El propietario puede anadirlas, editarlas y eliminarlas.
 *   * Se guardan en la base de datos; NUNCA en el catalogo de categorias.
 *   * La busqueda prioriza coincidencias exactas de la palabra clave.
 *   * Las opciones sugeridas se deduplican de forma global.
 */

export const MAX_DIRECTORY_KEYWORDS = 3;
export const MAX_DIRECTORY_KEYWORD_LENGTH = 60;

/** Normaliza una palabra clave para comparar/almacenar (minusculas, sin tildes). */
export function normalizeDirectoryKeyword(value: string | null | undefined): string {
  return normalizeDirectoryText(value);
}

/**
 * Limpia una lista de palabras clave: recorta, colapsa espacios, elimina vacias y
 * duplicadas (por forma normalizada), respeta el maximo y el largo.
 */
export function sanitizeDirectoryKeywords(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input) {
    if (typeof raw !== "string") continue;
    const trimmed = raw.trim().replace(/\s+/g, " ");
    if (!trimmed) continue;
    const clipped = trimmed.slice(0, MAX_DIRECTORY_KEYWORD_LENGTH);
    const normalized = normalizeDirectoryKeyword(clipped);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(clipped);
    if (result.length >= MAX_DIRECTORY_KEYWORDS) break;
  }
  return result;
}

export function isValidDirectoryKeywords(input: unknown): boolean {
  if (input == null) return true;
  if (!Array.isArray(input)) return false;
  return (
    input.length <= MAX_DIRECTORY_KEYWORDS &&
    input.every(
      (item) =>
        typeof item === "string" &&
        item.trim() !== "" &&
        item.trim().length <= MAX_DIRECTORY_KEYWORD_LENGTH,
    )
  );
}

export interface DirectoryKeywordSuggestion {
  /** Texto visible para el usuario (conserva tildes). */
  keyword: string;
  /** Forma normalizada usada para deduplicar. */
  normalized: string;
  /** Numero de negocios que usan esta palabra clave. */
  count: number;
}

/**
 * Agrupa y deduplica palabras clave de un conjunto de negocios, devolviendo las
 * mas frecuentes. Si se pasa `query`, filtra por coincidencia parcial y prioriza
 * la coincidencia exacta.
 */
export function aggregateKeywordSuggestions(
  keywords: ReadonlyArray<string>,
  query = "",
  limit = 8,
): DirectoryKeywordSuggestion[] {
  const normalizedQuery = normalizeDirectoryKeyword(query);
  const grouped = new Map<string, { keyword: string; count: number }>();

  for (const keyword of keywords) {
    const normalized = normalizeDirectoryKeyword(keyword);
    if (!normalized) continue;
    const existing = grouped.get(normalized);
    if (existing) {
      existing.count += 1;
      if (normalized < normalizeDirectoryKeyword(existing.keyword)) existing.keyword = keyword.trim();
    } else {
      grouped.set(normalized, { keyword: keyword.trim().replace(/\s+/g, " "), count: 1 });
    }
  }

  return [...grouped.entries()]
    .filter(([normalized]) => !normalizedQuery || normalized.includes(normalizedQuery))
    .map(([normalized, entry]) => ({ keyword: entry.keyword, normalized, count: entry.count }))
    .sort((a, b) => {
      if (normalizedQuery) {
        const aExact = a.normalized === normalizedQuery ? 0 : 1;
        const bExact = normalizedQuery === b.normalized ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
      }
      if (a.count !== b.count) return b.count - a.count;
      return a.normalized.localeCompare(b.normalized, "es");
    })
    .slice(0, limit);
}

/** `true` si alguna palabra clave normalizada coincide exactamente con `query`. */
export function hasExactKeywordMatch(
  keywords: ReadonlyArray<string> | null | undefined,
  query: string | null | undefined,
): boolean {
  const normalized = normalizeDirectoryKeyword(query);
  if (!normalized) return false;
  return (keywords ?? []).some((keyword) => normalizeDirectoryKeyword(keyword) === normalized);
}
